/**
 * FE#318 — open the extension campaigns overlay BEFORE routing it.
 *
 * The extension gates the entire campaigns panel on `CampaignReducer.open`
 * (ConditionalWrapper in blueticks.tsx). The launch handlers used to call
 * `campaignsMemoryRouter.navigate(...)` BEFORE dispatching `openBulkDashboard()`,
 * with no error isolation — so if `navigate()` threw synchronously, the open
 * dispatch never ran and the panel silently never opened (César's 5.9.0
 * "clicking Create new campaign / Manage Campaigns does nothing"). This helper
 * enforces the safe order — set the surface, OPEN the panel, THEN navigate inside
 * a try/catch — so a router failure can never keep the panel shut.
 *
 * #651 — `setSurface` is isolated the same way: it runs reducers (setActiveTab has an
 * analytics side-effect in an extraReducer), and a throw there used to abort the click
 * before `openPanel`, i.e. a dead click with no panel and no diagnostics. `openPanel` is
 * isolated too (a throw there used to skip `navigate`), and `runLaunchStep` guards the
 * handler's other steps — every throw is reported as a `handler_error` with its step.
 */
import { noteUiClick, reportUiError } from '@common-services/ui-stall-reporter';

export interface CampaignLaunchIO {
  /** Set which surface/tab the panel should show (safe redux dispatches). */
  setSurface: () => void;
  /**
   * Make the panel visible — `dispatch(openBulkDashboard())`. MUST run before
   * `navigate`, and must not depend on it, so panel visibility is unconditional.
   */
  openPanel: () => void;
  /** Point the in-memory router at the surface (`campaignsMemoryRouter.navigate`). May throw. */
  navigate: () => void;
  /** Called if `navigate()` throws; the panel is already open regardless. */
  onNavigateError?: (error: unknown) => void;
  /** Called if `setSurface()` throws; the panel still opens (on its previous surface). */
  onSurfaceError?: (error: unknown) => void;
  /** Called if `openPanel()` throws; the router is still pointed at the surface. Without it the
   *  throw is reported as handler_error (action `campaignLaunch`, step `openPanel`). */
  onOpenPanelError?: (error: unknown) => void;
}

/** Run one step of a top-bar click handler; a throw is reported (handler_error + step) and swallowed. */
export function runLaunchStep(action: string, step: string, fn: () => void): void {
  try {
    fn();
  } catch (error) {
    reportUiError('handler_error', action, error, { step });
  }
}

export function launchCampaignSurface(io: CampaignLaunchIO): void {
  try {
    io.setSurface();
  } catch (error) {
    io.onSurfaceError?.(error);
  }
  // OPEN the panel before touching the router — the panel's visibility must never
  // depend on the navigate succeeding.
  try {
    io.openPanel();
  } catch (error) {
    // Swallowed so navigate still runs — so it must never be swallowed silently.
    if (io.onOpenPanelError) io.onOpenPanelError(error);
    else reportUiError('handler_error', 'campaignLaunch', error, { step: 'openPanel' });
  }
  try {
    io.navigate();
  } catch (error) {
    io.onNavigateError?.(error);
  }
}

/** What the top-bar campaign buttons do, injected so the click wiring is testable without rendering. */
export interface CampaignLaunchDeps {
  /** Arm the ~3s mount watch for this action (#651 mount_timeout). */
  watchMount: (action: string) => void;
  closeTasksPanel: () => void;
  /** Point the panel at the campaigns list. */
  setManageSurface: () => void;
  /** Point the panel at the new-campaign flow. */
  setNewCampaignSurface: () => void;
  openPanel: () => void;
  navigate: (path: string) => void;
  /** Close the top-bar menu. */
  handleClose: () => void;
  /** Orphan guard check, run first: false = orphaned content script (the guard shows the reload banner). */
  isAlive?: () => boolean;
}

/**
 * #651 — the "Manage campaigns" / "Create new campaign" click handlers. The watch is armed
 * BEFORE launching so a throw anywhere below is still reported; every step is guarded, so a
 * throw is reported as handler_error (with its step) and the click carries on to open the panel.
 */
export function createCampaignLaunchHandlers(deps: CampaignLaunchDeps): { manageCampaigns: () => void; newCampaign: () => void } {
  const launch = (action: string, setSurface: () => void, path: string) => {
    runLaunchStep(action, 'noteClick', () => noteUiClick(action));
    // An orphaned script can't open anything (and would add a mount_timeout on top of the banner).
    if (deps.isAlive && !deps.isAlive()) {
      runLaunchStep(action, 'handleClose', deps.handleClose);
      return;
    }
    runLaunchStep(action, 'watchMount', () => deps.watchMount(action));
    runLaunchStep(action, 'setIsTasksPanelOpen', deps.closeTasksPanel);
    launchCampaignSurface({
      setSurface,
      openPanel: deps.openPanel,
      navigate: () => deps.navigate(path),
      onSurfaceError: (e) => {
        console.error(`[campaign-launch] ${action}: setSurface failed; opening the panel anyway`, e);
        reportUiError('handler_error', action, e, { step: 'setSurface' });
      },
      onOpenPanelError: (e) => reportUiError('handler_error', action, e, { step: 'openPanel' }),
      onNavigateError: (e) => {
        console.error(`[campaign-launch] ${action}: router navigate failed; panel already open`, e);
        reportUiError('handler_error', action, e, { step: 'navigate' });
      },
    });
    runLaunchStep(action, 'handleClose', deps.handleClose);
  };
  return {
    // FE#318: "Manage campaigns" always lands on the list.
    manageCampaigns: () => launch('topBtnManageCampaigns', deps.setManageSurface, '/campaigns'),
    // Same surface the list's "New campaign" button opens.
    newCampaign: () => launch('topBtnLaunchCampaign', deps.setNewCampaignSurface, '/campaigns/new'),
  };
}
